/**
 * The hidden master (data/hiddenMaster.ts, docs/systems/FUN_P1.md section 2.3): ผู้เฒ่าเหยียบเมฆ is taken out of the world
 * until the player has seen `SECRET.vistasForMaster` vistas, then sits on the lone spire. His test is found by doing,
 * with no button: stand on the spire top, step off, and come down to the shrine's roof (reached at its height) without touching the ground
 * (river stones do not count as ground). The pass pays แต้มฝึกปรือ and a title, once. Mode `secret:master`: npc
 * (a spar after the descent) and pvp (a race) are decided by the sign-up; the spar and the race are not built yet,
 * so both modes pay the same test today. Nothing here draws a die.
 */
import { qgManualId } from '../data/qinggongDrops';
import { MASTER } from '../data/hiddenMaster';
import { SECRET } from '../data/secrets';
import { floorAt, PX_METRES } from '../map';
import { toCore } from '../region/core';
import type { Entity } from '../world';
import { modesOf } from './modes';
import type { SystemHost, WorldSystem } from './types';

type Phase = 'idle' | 'armed' | 'down';

export class HiddenMasterSystem implements WorldSystem {
  readonly name = 'hiddenMaster';
  private phase: Phase = 'idle';
  private master: Entity | null = null;
  private mode: 'npc' | 'pvp' = 'npc';

  constructor(private readonly h: SystemHost) {}

  /** The master has shown himself: enough vistas seen. */
  open(): boolean {
    return (this.h.world.progress.secrets?.vistas.length ?? 0) >= SECRET.vistasForMaster;
  }

  /** The mode the test was armed in (the sign-up's pick, pvp falling back to npc). */
  testMode(): 'npc' | 'pvp' {
    return this.mode;
  }

  /** The test is passed (saved). */
  passed(): boolean {
    return !!this.h.world.progress.secrets?.master;
  }

  step(_dt: number): void {
    const w = this.h.world;
    const p = w.player;
    this.gate();
    if (w.map.zone !== MASTER.zone || p.dead || !this.open() || this.passed()) {
      this.phase = 'idle';
      return;
    }
    const lp = toCore(w.map, p.x, p.y);
    const alt = (floorAt(w.map, p.x, p.y) + p.z) * PX_METRES;
    const grounded = p.z <= 0;
    const onSpire = Math.hypot(lp.x - MASTER.spire.x, lp.y - MASTER.spire.y) <= MASTER.spireR && alt >= MASTER.spire.h - 2;
    if (this.phase === 'idle') {
      if (onSpire && grounded) {
        this.phase = 'armed';
        this.mode = modesOf(w)?.resolve('secret:master', undefined, true) ?? 'npc';
      }
      return;
    }
    if (this.phase === 'armed') {
      // Off the top, into the air: the descent has begun.
      if (!onSpire && !grounded) {
        this.phase = 'down';
        this.h.notice('ลงจากยอดเสาโดยไม่แตะพื้น ไปให้ถึงหลังคาศาลเจ้ากลางน้ำ');
      } else if (!onSpire) this.phase = 'idle';
      return;
    }
    // Coming down: reaching the roof passes it (on foot or in the air), the ground ends it.
    const atShrine = Math.hypot(lp.x - MASTER.shrine.x, lp.y - MASTER.shrine.y) <= MASTER.shrineR && Math.abs(alt - MASTER.roofH) <= MASTER.roofBand;
    if (atShrine) return this.pass();
    if (!grounded) return;
    if (onSpire) {
      this.phase = 'armed';
      return;
    }
    const onStone = alt < MASTER.roofH - MASTER.roofBand && MASTER.stones.some(([x, y]) => Math.hypot(lp.x - x, lp.y - y) <= MASTER.stoneR);
    if (onStone) return;
    this.phase = 'idle';
    this.h.notice('แตะพื้นแล้ว การทดสอบล้มเหลว ขึ้นไปบนยอดเสาแล้วลองอีกครั้ง');
  }

  private pass(): void {
    const w = this.h.world;
    this.phase = 'idle';
    const save = w.progress.secrets ?? { vistas: [], chests: {} };
    w.progress.secrets = { ...save, master: true };
    this.h.grantReward({ cultivation: MASTER.cultivation, title: MASTER.title });
    // His test also hands over the manual of the glide (QG-DROPS: a thing that trades).
    const goods = w.progress.trades.goods;
    goods[qgManualId('feather-fall')] = (goods[qgManualId('feather-fall')] ?? 0) + 1;
    this.h.emit({ type: 'qgDrop', kind: 'manual', art: 'feather-fall', n: 1, why: MASTER.name });
    this.h.emit({ type: 'secret', id: 'master', kind: 'master', refill: false, gold: 0, cultivation: MASTER.cultivation });
    this.h.notice(`ผ่านบททดสอบของ${MASTER.name} +${MASTER.cultivation} แต้มฝึกปรือ`);
  }

  /** Takes the master out of the world until the vistas are seen, and sets him back on the spire after. */
  private gate(): void {
    const w = this.h.world;
    if (!this.master) {
      this.master = [...w.entities.values()].find((e) => e.kind === 'npc' && e.name === MASTER.name) ?? null;
      if (!this.master) return;
    }
    const there = w.entities.has(this.master.id);
    if (this.open() && !there) {
      w.entities.set(this.master.id, this.master);
      this.h.notice(MASTER.appear);
    } else if (!this.open() && there) w.entities.delete(this.master.id);
  }
}
